import React, { useState, useEffect, useMemo } from 'react';
import {
  Form,
  Input,
  DatePicker,
  Select,
  Steps,
  Button,
  Space,
  App,
  Alert,
  Typography,
  Divider
} from 'antd';
import * as Icons from '@ant-design/icons';
import { trpc as api } from '@/lib/api';
import { TemplateSelector } from './TemplateSelector';
import type { WorkOrderTemplate } from './TemplateSelector';
import { EquipmentSelector } from './EquipmentSelector';
import { withDevModalFilePath, SelfAssignButton, NumericInput } from '@/components/common';
import dayjs from 'dayjs';
import { useAuthStore } from '@/stores/authStore';

const { Modal } = withDevModalFilePath('client/src/components/schedule/ScheduleCreateModal.tsx');

const { Step } = Steps;
const { TextArea } = Input;
const { Title } = Typography;

interface ScheduleCreateModalProps {
  open: boolean;
  onClose: () => void;
  onSuccess: (result: { scheduleId: string }) => void;
  showSteps?: boolean;
}

interface FormValues {
  name: string;
  scheduledDate: dayjs.Dayjs | null;
  workTypeCategoryId?: string;
  workOrderTemplateId: string | null;
  selectedEquipmentIds: string[];
  assignedUserId?: string;
  duration?: number;
  priority?: string;
  description?: string;
}

export const ScheduleCreateModal: React.FC<ScheduleCreateModalProps> = ({
  open,
  onClose,
  onSuccess,
  showSteps = false
}) => {
  const { message } = App.useApp();
  const [form] = Form.useForm<FormValues>();
  const assignedUserIdWatch = Form.useWatch('assignedUserId', form);
  const [currentStep, setCurrentStep] = useState(0);
  const [selectedTemplate, setSelectedTemplate] = useState<WorkOrderTemplate | null>(null);
  const [selectedEquipmentIds, setSelectedEquipmentIds] = useState<string[]>([]);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const { user } = useAuthStore();

  // ユーザーリストを取得
  const { data: users, isLoading: isLoadingUsers } = api.user.list.useQuery(
    {
      companyId: user?.companyId,
      page: 1,
      limit: 200
    },
    { enabled: open }
  );

  const userList = useMemo(() => {
    if (!users) {
      return [];
    }
    const fromData = (users as { data?: Array<{ id: string; name?: string | null; email?: string | null }> }).data;
    if (Array.isArray(fromData)) {
      return fromData;
    }
    const fromItems = (users as { items?: Array<{ id: string; name?: string | null; email?: string | null }> }).items;
    if (Array.isArray(fromItems)) {
      return fromItems;
    }
    return [];
  }, [users]);

  // 作業カテゴリを取得
  const { data: workTypeCategories = [], isLoading: isLoadingCategories } = api.workTypeCategory.list.useQuery(
    {
      includeInactive: false,
      sortBy: 'display_order',
      sortOrder: 'asc',
    },
    { enabled: open }
  );

  // アイコンコンポーネントを取得するヘルパー関数
  const getIconComponent = (iconName?: string | null) => {
    if (!iconName) return null;
    const iconKey = iconName as keyof typeof Icons;
    const IconComponent = Icons[iconKey] as React.ComponentType<{ style?: React.CSSProperties }> | undefined;
    return IconComponent ? <IconComponent style={{ fontSize: 14 }} /> : null;
  };

  // workTypeCategoryId から workType を推論（後方互換性のため）
  const inferWorkTypeFromCategory = (categoryId?: string): 'inspection' | 'repair' | 'replacement' | 'maintenance' => {
    if (!categoryId) return 'maintenance';

    const category = workTypeCategories.find(c => c.id === categoryId);
    if (!category) return 'maintenance';

    const label = category.categoryLabel.toLowerCase();
    if (label.includes('点検') || label.includes('inspection')) return 'inspection';
    if (label.includes('修理') || label.includes('repair')) return 'repair';
    if (label.includes('交換') || label.includes('replacement')) return 'replacement';
    return 'maintenance';
  };

  // スケジュール作成ミューテーション
  const createScheduleMutation = api.schedule.create.useMutation({
    onSuccess: (schedule) => {
      message.success('スケジュールを作成しました');
      onSuccess({ scheduleId: schedule.id });
      handleClose();
    },
    onError: (error) => {
      setSubmitError(error.message || '作成に失敗しました');
      message.error('スケジュールの作成に失敗しました');
    }
  });

  // モーダルが開かれた時の初期化
  useEffect(() => {
    if (open) {
      form.resetFields();
      setCurrentStep(0);
      setSelectedTemplate(null);
      setSelectedEquipmentIds([]);
      setSubmitError(null);
    }
  }, [open, form]);

  useEffect(() => {
    form.setFieldValue('workTypeCategoryId', selectedTemplate?.workTypeCategoryId ?? undefined);
  }, [selectedTemplate, form]);

  // フォームの送信
  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();

      if (!selectedEquipmentIds.length) {
        message.error('作業対象の設備を少なくとも1件選択してください');
        return;
      }

      const primaryEquipmentId = selectedEquipmentIds[0];
      const scheduledDate = values.scheduledDate
        ? values.scheduledDate.toDate()
        : new Date();
      const scheduledDuration = values.duration
        ? Math.round(values.duration * 60)
        : undefined;

      const resolvedCategoryId =
        selectedTemplate?.workTypeCategoryId ?? values.workTypeCategoryId ?? undefined;

      const resolvedWorkType =
        selectedTemplate?.workType ??
        inferWorkTypeFromCategory(resolvedCategoryId);

      await createScheduleMutation.mutateAsync({
        equipmentId: primaryEquipmentId,
        workOrderTemplateId: selectedTemplate?.id ?? undefined,
        workTypeCategoryId: resolvedCategoryId,
        scheduleType: 'scheduled',
        workType: resolvedWorkType,
        priority: (values.priority ??
          selectedTemplate?.priority ??
          'medium') as 'low' | 'medium' | 'high' | 'urgent',
        status: 'scheduled',
        title: values.name,
        description: values.description ?? undefined,
        scheduledDate,
        scheduledDuration,
        assignedTo: values.assignedUserId ?? undefined,
        selectedEquipments: Array.from(new Set(selectedEquipmentIds)).map(
          (equipmentId) => ({ equipmentId })
        )
      });
    } catch (error) {
      // バリデーションエラーの場合
      if (error instanceof Error) {
        console.error('Validation error:', error);
      }
    }
  };

  // モーダルを閉じる
  const handleClose = () => {
    form.resetFields();
    setCurrentStep(0);
    setSelectedTemplate(null);
    setSelectedEquipmentIds([]);
    setSubmitError(null);
    onClose();
  };

  // ステップの内容
  const renderStepContent = () => {
    switch (currentStep) {
      case 0:
        // 基本情報
        return (
          <>
            <Form.Item
              label="スケジュール名"
              name="name"
              rules={[{ required: true, message: 'スケジュール名を入力してください' }]}
            >
              <Input placeholder="例: 月次点検 - 2024年1月" />
            </Form.Item>

            <Form.Item
              label="実施予定日"
              name="scheduledDate"
              rules={[{ required: true, message: '実施予定日を選択してください' }]}
            >
              <DatePicker
                showTime
                format="YYYY-MM-DD HH:mm"
                style={{ width: '100%' }}
                placeholder="実施予定日を選択"
              />
            </Form.Item>

            <Form.Item
              label="優先度"
              name="priority"
              initialValue="medium"
            >
              <Select>
                <Select.Option value="low">低</Select.Option>
                <Select.Option value="medium">中</Select.Option>
                <Select.Option value="high">高</Select.Option>
                <Select.Option value="urgent">緊急</Select.Option>
              </Select>
            </Form.Item>

            <Form.Item
              label="作業カテゴリ"
              name="workTypeCategoryId"
              rules={[{ required: false }]}
            >
              <Select
                placeholder="作業カテゴリを選択（任意）"
                allowClear
                disabled={!!selectedTemplate}
                loading={isLoadingCategories}
              >
                {workTypeCategories.map(cat => (
                  <Select.Option key={cat.id} value={cat.id}>
                    <Space size={4}>
                      {getIconComponent(cat.icon)}
                      {cat.color && (
                        <div
                          style={{
                            width: 12,
                            height: 12,
                            backgroundColor: cat.color,
                            borderRadius: '2px',
                            border: '1px solid #d9d9d9',
                            display: 'inline-block'
                          }}
                        />
                      )}
                      {cat.categoryLabel}
                    </Space>
                  </Select.Option>
                ))}
              </Select>
            </Form.Item>

            <Form.Item
              label="作業時間（時間）"
              name="duration"
            >
              <NumericInput min={0.5} max={24} step={0.5} style={{ width: '100%' }} />
            </Form.Item>

            <Form.Item label="担当者">
              <Space.Compact style={{ width: '100%' }}>
                <Form.Item name="assignedUserId" noStyle>
                  <Select
                    placeholder="担当者を選択"
                    allowClear
                    showSearch
                    optionFilterProp="children"
                    loading={isLoadingUsers}
                    style={{ flex: 1 }}
                  >
                    {userList.map((user) => (
                      <Select.Option key={user.id} value={user.id}>
                        {user.name || user.email || user.id}
                      </Select.Option>
                    ))}
                  </Select>
                </Form.Item>
                <SelfAssignButton
                  currentValues={assignedUserIdWatch ? [assignedUserIdWatch] : []}
                  onAssign={(userId) => {
                    form.setFieldValue('assignedUserId', userId);
                  }}
                />
              </Space.Compact>
            </Form.Item>

            <Form.Item
              label="説明"
              name="description"
            >
              <TextArea rows={3} placeholder="スケジュールの説明を入力" />
            </Form.Item>
          </>
        );

      case 1:
        // テンプレート選択
        return (
          <div>
            <Title level={5}>作業指示書</Title>
            <TemplateSelector
              value={selectedTemplate}
              onChange={setSelectedTemplate}
              allowClear
              showEquipmentDetail
            />
          </div>
        );

      case 2:
        // 設備選択
        return (
          <div>
            <Title level={5}>作業対象設備</Title>
            <EquipmentSelector
              templateId={selectedTemplate?.id || null}
              value={selectedEquipmentIds}
              onChange={setSelectedEquipmentIds}
              showSearch
              showDetails
              showValidation
            />
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <Modal
      title="新規スケジュール作成"
      open={open}
      onCancel={handleClose}
      width={800}
      footer={
        showSteps ? (
          <Space>
            {currentStep > 0 && (
              <Button onClick={() => setCurrentStep(currentStep - 1)}>
                前へ
              </Button>
            )}
            {currentStep < 2 ? (
              <Button type="primary" onClick={() => setCurrentStep(currentStep + 1)}>
                次へ
              </Button>
            ) : (
              <>
                <Button onClick={handleClose}>キャンセル</Button>
                <Button
                  type="primary"
                  onClick={handleSubmit}
                  loading={createScheduleMutation.isPending}
                >
                  作成
                </Button>
              </>
            )}
          </Space>
        ) : (
          <>
            <Button onClick={handleClose}>キャンセル</Button>
            <Button
              type="primary"
              onClick={handleSubmit}
              loading={createScheduleMutation.isPending}
            >
              作成
            </Button>
          </>
        )
      }
    >
      {submitError && (
        <Alert
          message="エラー"
          description={submitError}
          type="error"
          showIcon
          closable
          onClose={() => setSubmitError(null)}
          style={{ marginBottom: 16 }}
        />
      )}

      {showSteps && (
        <>
          <Steps current={currentStep} style={{ marginBottom: 24 }}>
            <Step title="基本情報" />
            <Step title="テンプレート選択" />
            <Step title="設備選択" />
          </Steps>
          <Divider />
        </>
      )}

      <Form
        form={form}
        layout="vertical"
        initialValues={{
          priority: 'medium'
        }}
      >
        {showSteps ? (
          renderStepContent()
        ) : (
          <>
            {/* 基本情報 */}
            <Title level={5}>基本情報</Title>
            <Form.Item
              label="スケジュール名"
              name="name"
              rules={[{ required: true, message: 'スケジュール名を入力してください' }]}
            >
              <Input placeholder="例: 月次点検 - 2024年1月" />
            </Form.Item>

            <Form.Item
              label="実施予定日"
              name="scheduledDate"
              rules={[{ required: true, message: '実施予定日を選択してください' }]}
            >
              <DatePicker
                showTime
                format="YYYY-MM-DD HH:mm"
                style={{ width: '100%' }}
                placeholder="実施予定日を選択"
              />
            </Form.Item>

            <Form.Item
              label="優先度"
              name="priority"
              initialValue="medium"
            >
              <Select>
                <Select.Option value="low">低</Select.Option>
                <Select.Option value="medium">中</Select.Option>
                <Select.Option value="high">高</Select.Option>
                <Select.Option value="urgent">緊急</Select.Option>
              </Select>
            </Form.Item>

            <Form.Item
              label="作業カテゴリ"
              name="workTypeCategoryId"
              rules={[{ required: false }]}
            >
              <Select
                placeholder="作業カテゴリを選択（任意）"
                allowClear
                disabled={!!selectedTemplate}
                loading={isLoadingCategories}
              >
                {workTypeCategories.map(cat => (
                  <Select.Option key={cat.id} value={cat.id}>
                    <Space size={4}>
                      {getIconComponent(cat.icon)}
                      {cat.color && (
                        <div
                          style={{
                            width: 12,
                            height: 12,
                            backgroundColor: cat.color,
                            borderRadius: '2px',
                            border: '1px solid #d9d9d9',
                            display: 'inline-block'
                          }}
                        />
                      )}
                      {cat.categoryLabel}
                    </Space>
                  </Select.Option>
                ))}
              </Select>
            </Form.Item>

            <Form.Item
              label="作業時間（時間）"
              name="duration"
            >
              <NumericInput min={0.5} max={24} step={0.5} style={{ width: '100%' }} />
            </Form.Item>

            <Form.Item label="担当者">
              <Space.Compact style={{ width: '100%' }}>
                <Form.Item name="assignedUserId" noStyle>
                  <Select
                    placeholder="担当者を選択"
                    allowClear
                    showSearch
                    optionFilterProp="children"
                    loading={isLoadingUsers}
                    style={{ flex: 1 }}
                  >
                    {userList.map((user) => (
                      <Select.Option key={user.id} value={user.id}>
                        {user.name || user.email || user.id}
                      </Select.Option>
                    ))}
                  </Select>
                </Form.Item>
                <SelfAssignButton
                  currentValues={assignedUserIdWatch ? [assignedUserIdWatch] : []}
                  onAssign={(userId) => {
                    form.setFieldValue('assignedUserId', userId);
                  }}
                />
              </Space.Compact>
            </Form.Item>

            <Form.Item
              label="説明"
              name="description"
            >
              <TextArea rows={3} placeholder="スケジュールの説明を入力" />
            </Form.Item>

            <Divider />

            {/* テンプレート選択 */}
            <Title level={5}>作業指示書</Title>
            <TemplateSelector
              value={selectedTemplate}
              onChange={setSelectedTemplate}
              allowClear
              showEquipmentDetail
            />

            <Divider />

            {/* 設備選択 */}
            <Title level={5}>作業対象設備</Title>
            <EquipmentSelector
              templateId={selectedTemplate?.id || null}
              value={selectedEquipmentIds}
              onChange={setSelectedEquipmentIds}
              showSearch
              showDetails
              showValidation
            />
          </>
        )}
      </Form>
    </Modal>
  );
};
